import { isOneOf } from '../../helpers/is-one-of.js'

/**
 * Loan workspace sections that may host issues.
 * History / invite / MISMO are nav-only and cannot be targeted.
 */
export const LOAN_ISSUE_SECTIONS = [
  'overview',
  'borrowers',
  'financials',
  'credit',
  'pre-approval',
  'contract',
  'insurance',
  'property',
  'title',
  'conditions',
  'loan-estimate',
  'compliance',
  'closing-disclosure',
  'documents',
] as const
export type LoanIssueSection = (typeof LOAN_ISSUE_SECTIONS)[number]

/**
 * Field ids that exist in product UI for each section.
 * Add an id here in the same change that wires Field invalid chrome.
 */
export const LOAN_ISSUE_FIELDS = {
  overview: ['applicationDate', 'intentToProceedDate'],
  borrowers: [],
  financials: [],
  credit: [],
  'pre-approval': [
    'loanProgramId',
    'purchasePrice',
    'maxCltv',
    'rate',
    'backEndDti',
    'incomeDocumentation',
    'verification',
  ],
  conditions: [],
  contract: ['sellers', 'salesPrice'],
  insurance: [
    'selectedHazard',
    'selectedWind',
    'selectedFlood',
    'hazardDeductiblePercent',
    'windDeductiblePercent',
  ],
  property: ['appraisedValue'],
  title: [
    'currentOwner',
    'lastSaleDate',
    'lastSalePrice',
    'ownerPolicyAmount',
    'ownerProposedInsured',
    'lenderPolicyAmount',
    'lenderProposedInsured',
    'legalDescription',
  ],
  'loan-estimate': ['loanAmount', 'purchasePrice'],
  compliance: [],
  'closing-disclosure': [],
  documents: [],
} as const satisfies { [S in LoanIssueSection]: readonly string[] }

export type LoanIssueField<S extends LoanIssueSection> =
  (typeof LOAN_ISSUE_FIELDS)[S][number]

export function isLoanIssueSection(value: string): value is LoanIssueSection {
  return isOneOf(value, LOAN_ISSUE_SECTIONS)
}

export function isLoanIssueField(
  section: LoanIssueSection,
  field: string,
): boolean {
  const fields: readonly string[] = LOAN_ISSUE_FIELDS[section]
  return fields.includes(field)
}
